"use client";

import { useEffect, useMemo, useState } from "react";
import {
  AlertCircleIcon,
  ArrowLeftRightIcon,
  InfoIcon,
  PlusIcon,
  Trash2Icon,
} from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
} from "@/components/ui/field";
import { Label } from "@/components/ui/label";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { FlexRow } from "@/components/flex-row";
import { useVehicleBrandsQuery } from "@/lib/queries";

type TradeInAnswer = "" | "yes" | "no";

type TradeInVehicleDraft = {
  brandId: number | null;
  model: string;
};

type BrandOption = {
  value: number;
  label: string;
};

function RetailVehicleTradeInStep({
  onCanProceedChange,
}: {
  onCanProceedChange?: (canProceed: boolean) => void;
}) {
  const [answer, setAnswer] = useState<TradeInAnswer>("");
  const [vehicles, setVehicles] = useState<TradeInVehicleDraft[]>([]);

  const [deletingIndex, setDeletingIndex] = useState<number | null>(null);

  const isEnabled = answer === "yes";

  const vehicleBrandsQuery = useVehicleBrandsQuery(
    { sort: { name: "asc" } },
    undefined,
    { enabled: isEnabled },
  );

  const activeBrands = useMemo(
    () =>
      vehicleBrandsQuery.data?.vehicleBrands?.filter((b) => b.isActive) ?? [],
    [vehicleBrandsQuery.data?.vehicleBrands],
  );

  const brandOptions = useMemo<BrandOption[]>(
    () => activeBrands.map((b) => ({ value: b.id, label: b.name })),
    [activeBrands],
  );

  const canProceed = useMemo(() => {
    if (answer === "") return false;
    if (answer === "no") return true;

    if (vehicleBrandsQuery.isPending) {
      return false;
    }

    if (vehicleBrandsQuery.isError) {
      return false;
    }

    if (activeBrands.length === 0) return false;

    if (vehicles.length === 0) return false;

    return vehicles.every(
      (v) => v.brandId !== null && v.model.trim().length > 0,
    );
  }, [
    answer,
    vehicleBrandsQuery.isPending,
    vehicleBrandsQuery.isError,
    activeBrands.length,
    vehicles,
  ]);

  useEffect(() => {
    onCanProceedChange?.(canProceed);
  }, [canProceed, onCanProceedChange]);

  const showBrandsError = vehicleBrandsQuery.isError;

  const showMissingBrands =
    answer === "yes" &&
    !showBrandsError &&
    vehicleBrandsQuery.isSuccess &&
    activeBrands.length === 0;

  const showCardsError = showBrandsError || showMissingBrands;

  const handleAddVehicle = () => {
    setVehicles((prev) => {
      if (prev.length >= 3) return prev;
      return [...prev, { brandId: null, model: "" }];
    });
  };

  const handleDeleteVehicle = (index: number) => {
    setVehicles((prev) => prev.filter((_, i) => i !== index));
    setDeletingIndex(null);
  };

  const activeVehicleCount = vehicles.length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ArrowLeftRightIcon className="text-primary size-5" />
          Reprise de véhicule
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-6">
        <Field>
          <FieldLabel>Le client a-t-il un véhicule à reprendre ?</FieldLabel>
          <FieldContent>
            <RadioGroup
              className="flex flex-wrap gap-4"
              value={answer}
              onValueChange={(v) => {
                const nextAnswer = v as TradeInAnswer;
                setAnswer(nextAnswer);

                // Keep draft state synchronized with the user's "Oui/Non" choice.
                if (nextAnswer === "no") {
                  setVehicles([]);
                  return;
                }

                if (nextAnswer === "yes") {
                  setVehicles((prev) => {
                    if (prev.length > 0) return prev;
                    return [{ brandId: null, model: "" }];
                  });
                }
              }}
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem id="tradein-yes" value="yes" />
                <Label htmlFor="tradein-yes">Oui</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem id="tradein-no" value="no" />
                <Label htmlFor="tradein-no">Non</Label>
              </div>
            </RadioGroup>
          </FieldContent>
          <FieldDescription>
            Si vous choisissez <strong>Non</strong>, les informations de reprise
            saisies seront effacées (0 reprise).
          </FieldDescription>
        </Field>

        <Alert>
          <InfoIcon className="h-4 w-4" />
          <AlertDescription>
            Les informations de reprise sont nécessaires à la complétude du
            dossier. Vous pouvez reprendre jusqu&apos;à 3 véhicules.
          </AlertDescription>
        </Alert>

        {answer === "yes" ? (
          showCardsError ? (
            <div className="space-y-6">
              {showBrandsError ? (
                <Alert variant="destructive">
                  <AlertCircleIcon className="h-4 w-4" />
                  <AlertDescription>
                    Une erreur est survenue lors du chargement de la liste des
                    marques. Veuillez réessayer plus tard.
                  </AlertDescription>
                </Alert>
              ) : null}

              {showMissingBrands ? (
                <Alert variant="destructive">
                  <AlertCircleIcon className="h-4 w-4" />
                  <AlertDescription>
                    Aucune marque active n&apos;est configurée dans le menu
                    Système. Impossible de renseigner les reprises de véhicule.
                  </AlertDescription>
                </Alert>
              ) : null}
            </div>
          ) : (
            <>
              <FlexRow className="px-1">
                <div className="font-medium">Véhicule(s) à reprendre</div>
                <div className="text-muted-foreground">
                  {activeVehicleCount}/3 véhicules
                </div>
              </FlexRow>

              <div className="space-y-4">
                {vehicles.map((vehicle, index) => {
                  const selectedBrand = brandOptions.find(
                    (b) => b.value === vehicle.brandId,
                  );

                  return (
                    <div
                      key={index}
                      className="space-y-4 rounded-lg border p-4"
                    >
                      <FlexRow className="gap-4">
                        <div className="font-medium">Véhicule {index + 1}</div>

                        {vehicles.length > 1 ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground hover:text-destructive"
                            onClick={() => {
                              const hasData =
                                vehicle.brandId !== null ||
                                vehicle.model.trim().length > 0;

                              if (!hasData) {
                                setVehicles((prev) =>
                                  prev.filter((_, i) => i !== index),
                                );
                                return;
                              }

                              setDeletingIndex(index);
                            }}
                          >
                            <Trash2Icon className="size-4" />
                          </Button>
                        ) : (
                          <span />
                        )}
                      </FlexRow>

                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <Field>
                          <FieldLabel>Marque</FieldLabel>
                          <FieldContent>
                            {vehicleBrandsQuery.isPending ? (
                              <Skeleton className="h-9 w-full max-w-sm" />
                            ) : (
                              <Combobox
                                items={brandOptions}
                                itemToStringLabel={(c) => c.label}
                                value={selectedBrand ?? null}
                                onValueChange={(next) => {
                                  setVehicles((prev) => {
                                    const copy = [...prev];
                                    copy[index] = {
                                      brandId: next ? next.value : null,
                                      model: "",
                                    };
                                    return copy;
                                  });
                                }}
                              >
                                <ComboboxInput
                                  placeholder="Rechercher…"
                                  showClear={vehicle.brandId !== null}
                                  aria-label="Rechercher une marque"
                                />
                                <ComboboxContent>
                                  <ComboboxEmpty>
                                    Aucune marque trouvée.
                                  </ComboboxEmpty>
                                  <ComboboxList>
                                    {(o) => (
                                      <ComboboxItem key={o.value} value={o}>
                                        {o.label}
                                      </ComboboxItem>
                                    )}
                                  </ComboboxList>
                                </ComboboxContent>
                              </Combobox>
                            )}
                          </FieldContent>
                        </Field>

                        <Field>
                          <FieldLabel>Modèle</FieldLabel>
                          <FieldContent>
                            <Input
                              value={vehicle.model}
                              onChange={(e) => {
                                const next = e.target.value;
                                setVehicles((prev) => {
                                  const copy = [...prev];
                                  copy[index] = {
                                    ...copy[index],
                                    model: next,
                                  };
                                  return copy;
                                });
                              }}
                              placeholder="Saisir le modèle…"
                              disabled={vehicle.brandId === null}
                              aria-label="Modèle du véhicule de reprise"
                              className="max-w-sm"
                            />
                          </FieldContent>
                        </Field>
                      </div>
                    </div>
                  );
                })}

                {vehicles.length < 3 ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="flex w-full max-w-md items-center gap-2"
                    onClick={handleAddVehicle}
                  >
                    <PlusIcon className="size-4" />
                    Ajouter un véhicule de reprise
                  </Button>
                ) : null}
              </div>
            </>
          )
        ) : null}
      </CardContent>

      <AlertDialog
        open={deletingIndex !== null}
        onOpenChange={(o) => {
          if (!o) setDeletingIndex(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Supprimer une reprise de véhicule ?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Les informations saisies pour la carte sélectionnée seront
              supprimées.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deletingIndex === null) return;
                handleDeleteVehicle(deletingIndex);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

export { RetailVehicleTradeInStep };
